import { useAuthStore } from '~/store/auth.store';
import { useTenantStore } from '~/store/tenant.store';

/**
 * Standard feature-gate semantics:
 *   - returns `true`  when the flag is present in either store with a truthy value
 *     (feature is enabled and should be shown / accessible).
 *   - returns `false` otherwise (absent, falsy, or neither store has loaded yet).
 *
 * Lookup order: auth-store flags (per-user) win over tenant-store flags.
 *
 * NOTE on the backend contract — `/feature-flags/me` currently omits disabled flags
 * from its response entirely. So this hook returns `false` for both "explicitly
 * disabled" and "not defined as a flag" cases. Code that needs to distinguish the
 * two should consult `useIsFeatureFlagGated` (presence-only check) or maintain a
 * known-flag-key list (e.g. `FLAG_GATED_CODES` in `nav-main.tsx`).
 */
export function useFeatureFlag(flagKey: string): boolean {
  const authFlags = useAuthStore((state) => state.user?.featureFlags);
  const tenantFlags = useTenantStore((state) => state.config?.featureFlags);
  if (authFlags && flagKey in authFlags) return authFlags[flagKey] === true;
  if (tenantFlags && flagKey in tenantFlags) return tenantFlags[flagKey] === true;
  return false;
}

/** Returns whether a flag key is *gated* — i.e., present in either flag map (regardless
 *  of value). Use when "the backend has an opinion" vs "no opinion" matters. */
export function useIsFeatureFlagGated(flagKey: string): boolean {
  const authFlags = useAuthStore((state) => state.user?.featureFlags);
  const tenantFlags = useTenantStore((state) => state.config?.featureFlags);
  if (authFlags && flagKey in authFlags) return true;
  if (tenantFlags && flagKey in tenantFlags) return true;
  return false;
}
